import React, { useState, useEffect } from 'react';
import { getMyProfile, updateProfile } from '../../api/ProfileApis';
import './ProfileUpdate.css';

const ProfileUpdate = ({ onProfileUpdated, onCancel }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [picture, setPicture] = useState(null);
  const [preview, setPreview] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone_number: '',
    date_of_birth: '', bio: '', instructor_bio: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getMyProfile();
        setProfile(res.data);
        setForm({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          phone_number: res.data.phone_number || '',
          date_of_birth: res.data.date_of_birth?.split('T')[0] || '',
          bio: res.data.bio || '',
          instructor_bio: res.data.instructor_bio || ''
        });
        if (res.data.profile_picture) setPreview(res.data.profile_picture);
      } catch (e) { setError('Failed to load'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePicture = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return setError('Image file only');
    if (file.size > 5 * 1024 * 1024) return setError('Max 5MB');
    setPicture(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    setError('');
  };

  const removePicture = () => {
    setPicture(null);
    setPreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const data = new FormData();
      Object.keys(form).forEach(key => {
        if (form[key]) data.append(key, form[key]);
      });
      if (picture) data.append('profile_picture', picture);
      await updateProfile(data);
      setSuccess('✅ Updated!');
      setTimeout(onProfileUpdated, 1500);
    } catch (e) {
      setError('Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="update-loading">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  );

  if (error || !profile) return (
    <div className="update-error">
      <div className="err-icon">⚠️</div>
      <h3>Oops!</h3>
      <p>{error || 'No data'}</p>
      <button onClick={onCancel} className="btn-cancel">Back</button>
    </div>
  );

  return (
    <div className="update-wrap">
      <div className="update-header">
        <h1>✏️ Edit Profile</h1>
        <p>Update your info</p>
      </div>

      {error && <div className="alert err">{error}</div>}
      {success && <div className="alert ok">{success}</div>}

      <form onSubmit={handleSubmit} className="update-form">
        {/* Avatar */}
        <div className="avatar-section">
          <div className="avatar-wrap">
            {preview ? (
              <img src={preview} alt="Avatar" className="avatar-img" />
            ) : (
              <div className="avatar-placeholder">
                {profile?.first_name?.[0] || profile?.username?.[0] || 'U'}
              </div>
            )}
          </div>
          <div className="avatar-actions">
            <label className="upload-btn">
              {picture ? 'Change' : 'Upload'}
              <input type="file" accept="image/*" onChange={handlePicture} />
            </label>
            {(preview || profile?.profile_picture) && (
              <button type="button" className="remove-btn" onClick={removePicture}>
                Remove
              </button>
            )}
            <small>JPEG, PNG • Max 5MB</small>
          </div>
        </div>

        {/* Personal */}
        <div className="form-section">
          <h3>👤 Personal</h3>
          <div className="form-row">
            <input name="first_name" value={form.first_name} onChange={handleChange} placeholder="First name" />
            <input name="last_name" value={form.last_name} onChange={handleChange} placeholder="Last name" />
          </div>
          <div className="form-row">
            <input name="phone_number" value={form.phone_number} onChange={handleChange} placeholder="Phone" />
            <input type="date" name="date_of_birth" value={form.date_of_birth} onChange={handleChange} />
          </div>
        </div>

        {/* Bio */}
        <div className="form-section">
          <h3>📝 Bio</h3>
          <textarea name="bio" value={form.bio} onChange={handleChange} rows="3" placeholder="About you..." />
          {profile?.user_type === 'instructor' && (
            <textarea name="instructor_bio" value={form.instructor_bio} onChange={handleChange} rows="3" placeholder="Instructor bio..." />
          )}
        </div>

        {/* Account Info */}
        <div className="form-section">
          <h3>🔐 Account</h3>
          <div className="account-grid">
            <div className="account-item">
              <label>Username</label>
              <p>{profile?.username}</p>
            </div>
            <div className="account-item">
              <label>Email</label>
              <p>{profile?.email}</p>
            </div>
            <div className="account-item">
              <label>Type</label>
              <span className={`badge-${profile?.user_type || 'student'}`}>{profile?.user_type_display}</span>
            </div>
            <div className="account-item">
              <label>Verified</label>
              <span className={profile?.email_verified ? 'v-yes' : 'v-no'}>
                {profile?.email_verified ? '✅' : '⏳'}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="form-actions">
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Saving...' : '💾 Save'}
          </button>
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileUpdate;